/**
 * Shared SEO meta tag manager for all public portals.
 *
 * Imperatively updates `<head>` meta tags from the resolved branding
 * configuration. Google and other JavaScript-capable crawlers execute
 * the page's JS and will see the correct tags.
 *
 * Tags managed:
 * - `<title>`
 * - `<meta name="description">`
 * - `<meta property="og:title">`
 * - `<meta property="og:description">`
 * - `<meta property="og:image">` (when provided)
 * - `<link rel="canonical">` (when provided)
 * - `<meta name="robots">` (noindex for recommendation/private sites)
 * - `<script type="application/ld+json">` (structured data, when provided)
 *
 * @module shared/seo/SeoMeta
 */

import { useEffect } from 'react';

/** Props for the SeoMeta component. */
export interface SeoMetaProps {
  /** Page title. */
  title: string;
  /** Meta description. */
  description: string;
  /** Open Graph image URL. */
  ogImage?: string;
  /** Canonical URL for the page. */
  canonicalUrl?: string;
  /** JSON-LD structured data object. */
  structuredData?: Record<string, unknown>;
  /** When true, sets robots to "noindex, nofollow" (e.g. for Broker Client Recommendation Sites). */
  noIndex?: boolean;
}

/** Sets or creates a `<meta>` element in the document head. */
function setMeta(name: string, content: string, property?: boolean): void {
  const attr = property ? 'property' : 'name';
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Sets or creates a `<link rel="canonical">` in the document head. */
function setCanonical(url: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

/** Identifier for the JSON-LD script tag managed by this component. */
const JSONLD_ID = 'ipa-seo-jsonld';

/** Sets or creates a JSON-LD `<script>` block in the document head. */
function setJsonLd(data: Record<string, unknown>): void {
  let el = document.head.querySelector<HTMLScriptElement>(`script#${JSONLD_ID}`);
  if (!el) {
    el = document.createElement('script');
    el.id = JSONLD_ID;
    el.setAttribute('type', 'application/ld+json');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/** Removes a `<meta>` element from the document head if it exists. */
function removeMeta(name: string, property?: boolean): void {
  const attr = property ? 'property' : 'name';
  document.head.querySelector(`meta[${attr}="${name}"]`)?.remove();
}

/**
 * Headless component that keeps `<head>` meta tags synchronised with
 * the current page state. Renders nothing to the DOM.
 */
export function SeoMeta({
  title,
  description,
  ogImage,
  canonicalUrl,
  structuredData,
  noIndex = false,
}: SeoMetaProps): null {
  // Serialize structuredData to avoid infinite re-renders when callers pass
  // inline object literals (new reference on every render). The stringified
  // value is used as the useEffect dependency instead of the object itself.
  const structuredDataJson = structuredData ? JSON.stringify(structuredData) : undefined;

  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);

    // Set or remove og:image depending on current prop value
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    } else {
      removeMeta('og:image', true);
    }

    // Set or remove canonical link
    if (canonicalUrl) {
      setCanonical(canonicalUrl);
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    }

    // Set or remove JSON-LD structured data
    if (structuredDataJson) {
      setJsonLd(JSON.parse(structuredDataJson) as Record<string, unknown>);
    } else {
      document.head.querySelector(`script#${JSONLD_ID}`)?.remove();
    }

    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');
  }, [title, description, ogImage, canonicalUrl, structuredDataJson, noIndex]);

  return null;
}
